import React, { ButtonHTMLAttributes, ReactNode } from 'react';

interface FuturisticButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'accent' | 'outline' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  glow?: boolean;
  animated?: boolean;
  icon?: ReactNode;
  iconPosition?: 'left' | 'right';
  fullWidth?: boolean;
  className?: string;
  children: ReactNode;
}

const FuturisticButton: React.FC<FuturisticButtonProps> = ({
  variant = 'primary',
  size = 'md',
  glow = false,
  animated = false,
  icon,
  iconPosition = 'left',
  fullWidth = false,
  className = '',
  children,
  ...props
}) => {
  const glowClasses = glow
    ? variant === 'accent'
      ? 'ring-2 ring-neu-accent/40 ring-offset-1 ring-offset-neu-bg'
      : 'ring-1 ring-neu-accent/25 ring-offset-1 ring-offset-neu-bg'
    : '';

  const baseClasses =
    'relative inline-flex items-center justify-center font-display font-semibold rounded-neu-sm transition-all duration-300 ease-out focus:outline-none focus:ring-2 focus:ring-neu-accent focus:ring-offset-2 select-none';

  const sizeClasses = {
    sm: 'px-4 py-2 text-sm min-h-[44px]',
    md: 'px-6 py-3 text-base min-h-[48px]',
    lg: 'px-8 py-4 text-lg min-h-[56px]',
  };

  const widthClasses = fullWidth ? 'w-full' : '';

  const getVariantClasses = () => {
    switch (variant) {
      case 'accent':
        return [
          'bg-neu-accent text-white',
          // Violet-tinted neumorphic shadows — dark purple bottom-right, light purple top-left
          'shadow-[6px_6px_12px_rgba(76,69,204,0.55),-6px_-6px_12px_rgba(144,138,255,0.45)]',
          'hover:shadow-[9px_9px_18px_rgba(76,69,204,0.65),-9px_-9px_18px_rgba(144,138,255,0.55)]',
          'hover:-translate-y-0.5',
          'active:translate-y-0.5',
          'active:shadow-[inset_4px_4px_8px_rgba(60,54,180,0.6),inset_-4px_-4px_8px_rgba(160,155,255,0.35)]',
        ].join(' ');
      case 'secondary':
        return [
          'bg-neu-bg text-neu-teal',
          'shadow-[5px_5px_10px_rgb(163,177,198,0.6),-5px_-5px_10px_rgba(255,255,255,0.5)]',
          'hover:shadow-[7px_7px_14px_rgb(163,177,198,0.7),-7px_-7px_14px_rgba(255,255,255,0.6)]',
          'hover:-translate-y-0.5',
          'active:translate-y-0.5',
          'active:shadow-[inset_4px_4px_8px_rgb(163,177,198,0.6),inset_-4px_-4px_8px_rgba(255,255,255,0.5)]',
        ].join(' ');
      case 'outline':
        return [
          'bg-neu-bg text-neu-accent border-2 border-neu-accent/70',
          'shadow-[5px_5px_10px_rgb(163,177,198,0.6),-5px_-5px_10px_rgba(255,255,255,0.5)]',
          'hover:border-neu-accent hover:bg-neu-accent hover:text-white',
          'hover:shadow-[6px_6px_12px_rgba(76,69,204,0.55),-6px_-6px_12px_rgba(144,138,255,0.45)]',
          'hover:-translate-y-0.5',
          'active:translate-y-0.5',
          'active:shadow-[inset_4px_4px_8px_rgb(163,177,198,0.6),inset_-4px_-4px_8px_rgba(255,255,255,0.5)]',
        ].join(' ');
      case 'ghost':
        return [
          'bg-transparent text-neu-accent',
          'hover:bg-neu-bg hover:shadow-[5px_5px_10px_rgb(163,177,198,0.6),-5px_-5px_10px_rgba(255,255,255,0.5)]',
          'active:shadow-[inset_3px_3px_6px_rgb(163,177,198,0.6),inset_-3px_-3px_6px_rgba(255,255,255,0.5)]',
        ].join(' ');
      default: // primary
        return [
          'bg-neu-bg text-neu-text',
          'shadow-[5px_5px_10px_rgb(163,177,198,0.6),-5px_-5px_10px_rgba(255,255,255,0.5)]',
          'hover:shadow-[7px_7px_14px_rgb(163,177,198,0.7),-7px_-7px_14px_rgba(255,255,255,0.6)]',
          'hover:-translate-y-0.5',
          'active:translate-y-0.5',
          'active:shadow-[inset_4px_4px_8px_rgb(163,177,198,0.6),inset_-4px_-4px_8px_rgba(255,255,255,0.5)]',
        ].join(' ');
    }
  };

  return (
    <button
      className={`${baseClasses} ${sizeClasses[size]} ${getVariantClasses()} ${glowClasses} ${widthClasses} ${className}`}
      {...props}
    >
      {icon && iconPosition === 'left' && <span className="mr-2">{icon}</span>}
      <span className="relative z-10">{children}</span>
      {icon && iconPosition === 'right' && <span className="ml-2">{icon}</span>}
    </button>
  );
};

export default FuturisticButton;
